<template>
    <b-card no-body class="nsp-card h-100">
        <b-card-header class="d-flex align-items-center">
            <span class="nsp-title">{{ room.moduleName }}</span>
            <span class="ml-auto nsp-temp">{{ room.temperature }}&#8451;</span>
        </b-card-header>

        <b-card-body>
            <div class="d-flex align-items-center mb-2">
                <b-button
                    size="sm"
                    :variant="room.thermostat.power ? 'danger' : 'outline-secondary'"
                    v-on:click="$emit('thermostat', { moduleId: room.moduleId, power: !room.thermostat.power })">
                    <i class="icon-power"></i>
                </b-button>
                <span class="ml-2 small text-muted">{{ $t('NSP_HEATING') }}</span>

                <b-badge v-if="room.heating" variant="danger" class="ml-2">{{ $t('NSP_ACTIVE') }}</b-badge>

                <b-badge
                    v-if="room.ventDamper"
                    :variant="room.ventDamper.open ? 'info' : 'light'"
                    class="ml-auto">
                    {{ room.ventDamper.open ? $t('NSP_DAMPER_OPEN') : $t('NSP_DAMPER_CLOSED') }}
                    <template v-if="room.ventDamper.alwaysOn">&middot; {{ $t('NSP_DAMPER_ALWAYS') }}</template>
                </b-badge>
            </div>

            <div class="row text-center">
                <div v-bind:class="room.hasAc === false ? 'col-12' : 'col-6'"
                    v-on:mouseup="pushThermo" v-on:touchend="pushThermo">
                    <round-slider
                        v-model="setTemperature"
                        start-angle="0"
                        end-angle="+180"
                        line-cap="round"
                        min="10"
                        max="30"
                        step="0.5"
                        radius="70"/>
                    <div class="small text-muted">{{ $t('NSP_HEATING') }}</div>
                </div>
                <div class="col-6" v-if="room.hasAc !== false"
                    v-on:mouseup="pushAcTemp" v-on:touchend="pushAcTemp">
                    <round-slider
                        v-model="acTemperature"
                        start-angle="0"
                        end-angle="+180"
                        line-cap="round"
                        min="15"
                        max="30"
                        step="0.5"
                        radius="70"/>
                    <div class="small text-muted">{{ $t('NSP_AC') }}</div>
                </div>
            </div>

            <div v-if="room.hasAc !== false" class="d-flex align-items-center mt-2">
                <b-button
                    size="sm"
                    :variant="room.boost ? 'warning' : 'outline-secondary'"
                    v-on:click="$emit('boost', { moduleId: room.moduleId, on: !room.boost })">
                    <i class="mdi mdi-rocket-launch"></i>
                    {{ $t('NSP_BOOST') }}
                </b-button>
                <span class="ml-2 small text-muted">
                    {{ room.boost ? $t('NSP_BOOST_UNTIL', { time: boostEndsAt }) : $t('NSP_BOOST_HINT') }}
                </span>
            </div>

            <template v-for="page in room.pages">
                <div :key="page.key" class="mt-3">
                    <div class="text-muted small mb-1">{{ page.title }}</div>

                    <template v-for="entity in page.entities">
                        <shutter-row
                            v-if="entity.type === 'shutter'"
                            :key="entity.moduleId"
                            :entity="entity"
                            v-on:up="$emit('shutter-up', $event)"
                            v-on:stop="$emit('shutter-stop', $event)"
                            v-on:down="$emit('shutter-down', $event)"/>
                        <light-row
                            v-else
                            :key="entity.moduleId"
                            :entity="entity"
                            v-on:toggle="$emit('light-toggle', $event)"
                            v-on:dim="$emit('light-dim', $event)"
                            v-on:color="$emit('light-color', $event)"
                            v-on:effect="$emit('light-effect', $event)"/>
                    </template>
                </div>
            </template>
        </b-card-body>
    </b-card>
</template>

<script>
import LightRow from './LightRow.vue';
import ShutterRow from './ShutterRow.vue';

export default
{
    name: 'RoomCard',
    components:
    {
        'light-row': LightRow,
        'shutter-row': ShutterRow,
    },
    props:
    {
        room:
        {
            type: Object,
            required: true,
        },
    },
    data()
    {
        return {
            setTemperature: this.room.thermostat.setTemperature,
            acTemperature: this.room.acTemperature,
        };
    },
    computed:
    {
        // when the boost gives up of its own accord, so the card says how long
        // the rest of the house stays shut rather than only that it is
        boostEndsAt()
        {
            const me = this;

            if (me.room.boost == null)
            {
                return '';
            }

            const at = new Date(me.room.boost.endsAt);

            return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
        },
    },
    watch:
    {
        'room.thermostat.setTemperature': function roomSetTemperature(value)
        {
            this.setTemperature = value;
        },
        'room.acTemperature': function roomAcTemperature(value)
        {
            this.acTemperature = value;
        },
    },
    methods:
    {
        pushThermo()
        {
            const me = this;

            if (me.setTemperature !== me.room.thermostat.setTemperature)
            {
                me.$emit('thermostat', { moduleId: me.room.moduleId, setTemperature: me.setTemperature });
            }
        },
        pushAcTemp()
        {
            const me = this;

            if (me.acTemperature !== me.room.acTemperature)
            {
                me.$emit('actemp', { moduleId: me.room.moduleId, temperature: me.acTemperature });
            }
        },
    },
};
</script>
